/**
 * Losi Model Routing
 *
 * Shared backend model targets for Losi-branded model IDs.
 *
 * Primary responsibilities:
 * - Map Losi Max and non-mini Losi chat paths to the selected backend
 * - Map Losi Mini chat paths to the selected mini backend
 *
 * Dependencies:
 * - lib/anthropic/model-capabilities
 * - lib/losi/text-provider-mode
 */

import {
  CLAUDE_FABLE_5_1_MODEL_ID,
  CLAUDE_FABLE_5_MODEL_ID,
  CLAUDE_OPUS_5_MODEL_ID,
  CLAUDE_HAIKU_4_5_MODEL_ID,
  CLAUDE_OPUS_4_5_MODEL_ID,
  CLAUDE_OPUS_4_6_MODEL_ID,
  CLAUDE_SONNET_4_5_MODEL_ID,
  CLAUDE_SONNET_5_MODEL_ID
} from '@/lib/anthropic/model-capabilities'
import {
  getLosiCopyAzureMaxModel,
  getLosiCopyAzureMiniModel,
  resolveLosiTextProviderMode
} from '@/lib/losi/text-provider-mode'

export const LOSI_MAX_BACKEND_MODEL = CLAUDE_SONNET_5_MODEL_ID
export const LOSI_MINI_BACKEND_MODEL = CLAUDE_HAIKU_4_5_MODEL_ID
export const LOSI_FABLE_BACKEND_MODEL = CLAUDE_FABLE_5_1_MODEL_ID
export const LOSI_CLAUDE_DAILY_LIMIT_FALLBACK_MODEL = CLAUDE_SONNET_4_5_MODEL_ID
export const LOSI_CLAUDE_DAILY_LIMIT_FALLBACK_CHAIN = [
  CLAUDE_OPUS_4_6_MODEL_ID,
  CLAUDE_OPUS_4_5_MODEL_ID,
  CLAUDE_SONNET_4_5_MODEL_ID
] as const
export const LOSI_CLAUDE_DAILY_LIMIT_FALLBACK_MS = 5 * 60 * 1000

type LosiClaudeFallbackTier = 'max' | 'mini'

type LosiClaudeFallbackState = {
  expiresAt: number
  triggeredByModel: string
}

const COMPATIBLE_MINI_TEXT_MODELS = new Set([
  'gpt-4o-mini',
  'gpt-4.1-mini',
  'gpt-4.1-nano',
  'gpt-5-mini',
  'gpt-5.4-mini',
  'gpt-5-nano',
  'gpt-5.6-luna'
])

const COMPATIBLE_MAX_TEXT_MODELS = new Set([
  'gpt-4.1',
  'gpt-5.1',
  'gpt-5.2',
  'gpt-5.2-chat-latest',
  'gpt-5.2-pro',
  'gpt-5.4',
  'gpt-5.4-pro',
  'gpt-5.5',
  'gpt-5.6-sol',
  'gpt-5.6-terra',
  CLAUDE_OPUS_5_MODEL_ID,
  CLAUDE_FABLE_5_1_MODEL_ID,
  CLAUDE_FABLE_5_MODEL_ID
])

const losiClaudeDailyLimitFallbacks: Partial<
  Record<LosiClaudeFallbackTier, LosiClaudeFallbackState>
> = {}

// --------------

function getLosiClaudeFallbackTier(model: string): LosiClaudeFallbackTier | null {
  if (model === 'losi-mini' || model === LOSI_MINI_BACKEND_MODEL) {
    return 'mini'
  }

  if (
    model === 'losi-omega' ||
    model === 'losi-flash' ||
    model === LOSI_MAX_BACKEND_MODEL ||
    model === LOSI_FABLE_BACKEND_MODEL
  ) {
    return 'max'
  }

  return null
}

// --------------

function getActiveLosiClaudeDailyLimitFallback(
  model: string,
  now = Date.now()
) {
  const tier = getLosiClaudeFallbackTier(model)
  if (!tier) return null

  const fallback = losiClaudeDailyLimitFallbacks[tier]
  if (!fallback) return null

  if (fallback.expiresAt <= now) {
    delete losiClaudeDailyLimitFallbacks[tier]
    return null
  }

  return {
    tier,
    model: LOSI_CLAUDE_DAILY_LIMIT_FALLBACK_CHAIN[0],
    chain: LOSI_CLAUDE_DAILY_LIMIT_FALLBACK_CHAIN,
    expiresAt: fallback.expiresAt,
    triggeredByModel: fallback.triggeredByModel
  }
}

// --------------

function collectErrorMessages(error: unknown, seen = new Set<object>()): string[] {
  if (typeof error === 'string') return [error]
  if (!error || typeof error !== 'object') return []
  if (seen.has(error)) return []
  seen.add(error)

  const messages: string[] = []
  const record = error as Record<string, unknown>

  if (error instanceof Error && error.message) {
    messages.push(error.message)
  }

  for (const key of ['message', 'body', 'response', 'error', 'cause']) {
    const value = record[key]

    if (typeof value === 'string') {
      messages.push(value)
    } else if (value && typeof value === 'object') {
      messages.push(...collectErrorMessages(value, seen))
    }
  }

  return messages
}

// --------------

export function isLosiClaudeDailyLimitError(error: unknown) {
  return collectErrorMessages(error).some((message) =>
    message.toLowerCase().includes('too many tokens per day')
  )
}

/**
 * Bedrock rejects max_tokens above the target model's live output cap
 * ("maximum tokens you requested exceeds the model limit of N").
 * Capacity / daily-limit recovery must treat this as "try next model or
 * retry with a lower budget" — not a hard user-facing failure.
 */
export function isBedrockMaxOutputTokensError(error: unknown) {
  return collectErrorMessages(error).some((message) => {
    const lower = message.toLowerCase()
    return (
      lower.includes('maximum tokens you requested exceeds the model limit') ||
      /exceeds the model limit of \d+/i.test(message)
    )
  })
}

/** Parse `exceeds the model limit of N` when present; otherwise null. */
export function parseBedrockMaxOutputTokenLimit(error: unknown): number | null {
  for (const message of collectErrorMessages(error)) {
    const match = message.match(/exceeds the model limit of (\d+)/i)
    if (match?.[1]) {
      const limit = Number.parseInt(match[1], 10)
      if (Number.isFinite(limit) && limit > 0) return limit
    }
  }
  return null
}

/** True when recovery should hop models / lower max_tokens instead of failing. */
export function isRecoverableModelSwitchError(error: unknown) {
  return isLosiClaudeDailyLimitError(error) || isBedrockMaxOutputTokensError(error)
}

// --------------

export function activateLosiClaudeDailyLimitFallback(
  model: string,
  now = Date.now()
) {
  const tier = getLosiClaudeFallbackTier(model)
  if (!tier) return null

  const state = {
    expiresAt: now + LOSI_CLAUDE_DAILY_LIMIT_FALLBACK_MS,
    triggeredByModel: model
  } satisfies LosiClaudeFallbackState

  losiClaudeDailyLimitFallbacks[tier] = state

  return {
    tier,
    model: LOSI_CLAUDE_DAILY_LIMIT_FALLBACK_CHAIN[0],
    chain: LOSI_CLAUDE_DAILY_LIMIT_FALLBACK_CHAIN,
    expiresAt: state.expiresAt,
    triggeredByModel: state.triggeredByModel
  }
}

// --------------

export function resolveLosiClaudeDailyLimitFallbackModel(
  sourceModel: string,
  defaultModel: string,
  now = Date.now()
) {
  return getActiveLosiClaudeDailyLimitFallback(sourceModel, now)?.model ?? defaultModel
}

// --------------

export function resetLosiClaudeDailyLimitFallbackForTests() {
  delete losiClaudeDailyLimitFallbacks.max
  delete losiClaudeDailyLimitFallbacks.mini
}

function isCompatibleMiniTextModel(model: string) {
  return model === 'losi-mini' ||
    model === LOSI_MINI_BACKEND_MODEL ||
    COMPATIBLE_MINI_TEXT_MODELS.has(model)
}

function isCompatibleMaxTextModel(model: string) {
  return model === 'losi-omega' ||
    model === 'losi-flash' ||
    model === LOSI_MAX_BACKEND_MODEL ||
    COMPATIBLE_MAX_TEXT_MODELS.has(model)
}

export function getLosiMaxBackendModel() {
  if (resolveLosiTextProviderMode() === 'copy_azure') {
    return getLosiCopyAzureMaxModel()
  }

  return resolveLosiClaudeDailyLimitFallbackModel('losi-omega', LOSI_MAX_BACKEND_MODEL)
}

export function getLosiMiniBackendModel() {
  if (resolveLosiTextProviderMode() === 'copy_azure') {
    return getLosiCopyAzureMiniModel()
  }

  return resolveLosiClaudeDailyLimitFallbackModel('losi-mini', LOSI_MINI_BACKEND_MODEL)
}

export function resolveLosiCopyAzureTextModel(model: string) {
  if (isCompatibleMiniTextModel(model)) return getLosiCopyAzureMiniModel()
  if (isCompatibleMaxTextModel(model)) return getLosiCopyAzureMaxModel()

  return model
}

export function resolveLosiChatBackendModel(model: string) {
  if (model === 'losi-mini') return getLosiMiniBackendModel()

  if (model === 'losi-omega' || model === 'losi-flash') {
    return getLosiMaxBackendModel()
  }

  return model
}

export function resolveCompatibleAiTextModel(model: string) {
  if (isCompatibleMiniTextModel(model)) {
    return getLosiMiniBackendModel()
  }

  if (isCompatibleMaxTextModel(model)) {
    return getLosiMaxBackendModel()
  }

  return model
}

export function isAnthropicBackendModel(model: string) {
  return model.startsWith('claude-') ||
    model.startsWith('anthropic.claude-') ||
    model.includes('.anthropic.claude-')
}

export function isLosiMiniModel(model: string) {
  return model === 'losi-mini' ||
    model === LOSI_MINI_BACKEND_MODEL ||
    model === getLosiCopyAzureMiniModel()
}

export function isLosiMaxModel(model: string) {
  return model === 'losi-omega' ||
    model === 'losi-flash' ||
    model === LOSI_MAX_BACKEND_MODEL ||
    model === getLosiCopyAzureMaxModel()
}
